import React, { useEffect, useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, EmptyState } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Rocket, Clock, ChevronRight, ArrowLeft, Loader2, CheckCircle2,
  FileText, ClipboardCheck, Scale, Compass, ArrowRight, PenLine, X, Plus, Trash2,
} from "lucide-react";

export default function CaseStartersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/case-starters").then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="case-starters-page">
      <PageHeader
        eyebrow="La Première Étape"
        title="Case Starters"
        description="Guided flows that turn a real-life problem into a case workspace, a first-move legal notice, and an evidence checklist — all in one short wizard."
      />

      {loading ? (
        <div className="text-sm text-[#4A5568] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading starters…
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {items.map((s) => (
            <Link
              key={s.slug}
              to={`/app/case-starters/${s.slug}`}
              className="group border border-[#E8E3F0] bg-white p-6 hover:border-[#2A0F5A] transition-colors"
              data-testid={`starter-${s.slug}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 border border-[#C5A059] flex items-center justify-center">
                    <Rocket size={16} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <div className="label-eyebrow">{s.jurisdiction}</div>
                    <div className="text-[11px] uppercase tracking-widest text-[#4A5568]">{s.case_type}</div>
                  </div>
                </div>
                <ChevronRight size={18} className="text-[#4A5568] group-hover:text-[#2A0F5A] group-hover:translate-x-1 transition-all" />
              </div>
              <h2 className="font-serif text-2xl mb-1.5 tracking-tight">{s.title}</h2>
              <p className="text-sm text-[#4A5568] mb-4">{s.subtitle}</p>
              <div className="flex items-center gap-4 text-xs text-[#4A5568]">
                <span className="inline-flex items-center gap-1"><Clock size={11} /> ~{s.estimated_time_min} min</span>
                <span className="inline-flex items-center gap-1"><ClipboardCheck size={11} /> {(s.evidence_checklist || []).length} evidence items</span>
                <span className="inline-flex items-center gap-1"><FileText size={11} /> notice draft</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Wizard ----------------------------------------------------------
export function CaseStarterWizardPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [starter, setStarter] = useState(null);
  const [answers, setAnswers] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/case-starters/${slug}`).then((r) => setStarter(r.data))
      .catch(() => toast.error("Starter not found"));
  }, [slug]);

  const run = async () => {
    const missing = (starter?.questions || []).filter((q) => !answers[q.key]).map((q) => q.label);
    if (missing.length) return toast.error(`Please answer: ${missing[0]}`);
    setRunning(true);
    try {
      const r = await api.post(`/case-starters/${slug}/run`, { answers });
      setResult(r.data);
      toast.success("Case set up");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not run"); }
    finally { setRunning(false); }
  };

  if (!starter) return <div className="text-sm text-[#4A5568] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  if (result) {
    return (
      <div data-testid="starter-result-page">
        <Link to="/app/case-starters" className="label-eyebrow inline-flex items-center gap-1.5 mb-4 hover:text-[#2A0F5A]">
          <ArrowLeft size={12} /> All starters
        </Link>
        <PageHeader
          eyebrow="Ready to go"
          title={`${starter.title} — set up`}
          description="We've created the matter, case, first-move draft and uploaded the evidence checklist as tasks. Here's where to go next."
        />
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          <ResultCard icon={Scale} title="Case workspace" subtitle="Facts, parties, claims, hearings, communications" cta="Open case" to={`/app/cases/${result.case_id}`} testId="result-case-btn" />
          <ResultCard icon={FileText} title="First notice drafted" subtitle="Pre-filled from your answers — review, then send" cta="Open draft" to={`/app/drafts/${result.draft_id}`} testId="result-draft-btn" />
          <ResultCard icon={ClipboardCheck} title="Evidence checklist" subtitle={`${(result.task_ids || []).length} items to gather, in Deadlines`} cta="Open deadlines" to={`/app/calendar`} testId="result-tasks-btn" />
        </div>

        {/* Starter → Signature connector */}
        <SendForSignatureCard
          starter={starter}
          draftId={result.draft_id}
          navigate={navigate}
        />

        {result.next_steps?.length > 0 && (
          <div className="border border-[#E8E3F0] bg-white p-6">
            <div className="label-eyebrow mb-2">Next steps</div>
            <ol className="space-y-2 text-sm">
              {result.next_steps.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="label-eyebrow text-[#C5A059] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="starter-wizard-page">
      <Link to="/app/case-starters" className="label-eyebrow inline-flex items-center gap-1.5 mb-4 hover:text-[#2A0F5A]">
        <ArrowLeft size={12} /> All starters
      </Link>
      <PageHeader
        eyebrow={`${starter.jurisdiction} · ${starter.case_type}`}
        title={starter.title}
        description={starter.intro || starter.subtitle}
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="label-eyebrow">Tell us a few facts</div>
          {(starter.questions || []).map((q) => (
            <div key={q.key}>
              <Label className="label-eyebrow mb-1.5 block">{q.label}</Label>
              {q.type === "textarea" ? (
                <Textarea
                  value={answers[q.key] || ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))}
                  rows={3}
                  className="rounded-none border-[#E8E3F0]"
                  data-testid={`starter-q-${q.key}`}
                />
              ) : (
                <Input
                  type={q.type === "date" ? "date" : "text"}
                  value={answers[q.key] || ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))}
                  className="rounded-none border-[#E8E3F0]"
                  data-testid={`starter-q-${q.key}`}
                />
              )}
            </div>
          ))}

          <Button
            onClick={run}
            disabled={running}
            className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs"
            data-testid="run-starter-btn"
          >
            {running ? <><Loader2 size={14} className="mr-2 animate-spin" /> Setting up…</> : <>Set up my case <ArrowRight size={14} className="ml-2" /></>}
          </Button>
        </div>

        {/* Side preview */}
        <aside className="lg:col-span-1">
          <div className="sticky top-6 border border-[#E8E3F0] bg-[#FAFAF8] p-5 text-sm space-y-5">
            <div>
              <div className="label-eyebrow mb-2">What this starter produces</div>
              <ul className="space-y-1.5 text-[#2A0F5A]/90">
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-[#215a3b] mt-0.5 shrink-0" /> A matter + case workspace</li>
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-[#215a3b] mt-0.5 shrink-0" /> A first-move legal notice (pre-filled)</li>
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-[#215a3b] mt-0.5 shrink-0" /> Evidence checklist as deadlines</li>
              </ul>
            </div>
            <div>
              <div className="label-eyebrow mb-2">Evidence to gather</div>
              <ul className="space-y-1 text-xs text-[#4A5568] list-disc list-inside">
                {(starter.evidence_checklist || []).map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function ResultCard({ icon: Icon, title, subtitle, cta, to, testId }) {
  return (
    <Link to={to} className="group border border-[#E8E3F0] bg-white p-5 hover:border-[#2A0F5A] transition-colors" data-testid={testId}>
      <Icon size={20} className="text-[#C5A059] mb-3" strokeWidth={1.5} />
      <div className="font-serif text-lg leading-tight mb-1">{title}</div>
      <div className="text-xs text-[#4A5568] mb-4">{subtitle}</div>
      <div className="label-eyebrow text-[#2A0F5A] flex items-center gap-1 group-hover:gap-2 transition-all">
        {cta} <ArrowRight size={11} />
      </div>
    </Link>
  );
}


/**
 * SendForSignatureCard — Starter → Signature connector.
 *
 * Pulls the drafted notice content, lets the user add one or more signers,
 * then creates + sends an envelope in a single step. Takes "problem → notice"
 * all the way to "problem → signed notice" in one session.
 */
function SendForSignatureCard({ starter, draftId, navigate }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [signers, setSigners] = useState([{ name: "", email: "", role: "signer" }]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  const openModal = async () => {
    setOpen(true);
    if (!draft) {
      try {
        const r = await api.get(`/drafts/${draftId}`);
        setDraft(r.data);
      } catch { toast.error("Could not open the draft"); }
    }
  };

  const addSigner = () => setSigners((s) => [...s, { name: "", email: "", role: "signer" }]);
  const removeSigner = (i) => setSigners((s) => s.length > 1 ? s.filter((_, idx) => idx !== i) : s);
  const updateSigner = (i, patch) => setSigners((s) => s.map((x, idx) => idx === i ? { ...x, ...patch } : x));

  const sendIt = async () => {
    if (!draft) return toast.error("Draft is still loading");
    if (signers.some((s) => !s.name.trim() || !s.email.trim()))
      return toast.error("Every signer needs a name and an email");
    setBusy(true);
    try {
      // 1. Create envelope from the drafted notice
      const env = await api.post("/envelopes", {
        title: draft.title || `${starter.title} — First notice`,
        content: draft.content || "",
        signers: signers.map((s, i) => ({
          name: s.name.trim(),
          email: s.email.trim(),
          role: s.role,
          order: i + 1,
        })),
        ordered: true,
        message: `This notice was prepared through Marianne's ${starter.title} starter. Please review and sign.`,
      });
      // 2. Send it (invitations go out via Resend)
      const sendRes = await api.post(`/envelopes/${env.data.id}/send`);
      const delivery = sendRes.data?.email_delivery || [];
      const okCount = delivery.filter((d) => d.status === "sent").length;
      setSent({ envelope_id: env.data.id, delivered: okCount, total: delivery.length });
      toast.success(okCount
        ? `Envelope sent — ${okCount} invitation${okCount === 1 ? "" : "s"} delivered`
        : "Envelope sent (emails stubbed — no key)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#2A0F5A] bg-white p-6 mb-10" data-testid="starter-send-signature-card">
      <div className="grid md:grid-cols-3 gap-6 items-center">
        <div className="md:col-span-2">
          <div className="label-eyebrow text-[#C5A059] mb-1.5">One step away</div>
          <h3 className="font-serif text-2xl leading-tight mb-2">
            Send this notice for signature.
          </h3>
          <p className="text-sm text-[#4A5568]">
            Turn the drafted notice into a signed envelope. Add the counterparty, we'll email the signing link, and the whole thing lands in <b>Signatures</b> with an audit trail and SHA-256 integrity.
          </p>
        </div>
        <div className="md:col-span-1 flex md:justify-end">
          {sent ? (
            <Button
              onClick={() => navigate(`/app/signatures/${sent.envelope_id}`)}
              className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs"
              data-testid="open-envelope-btn"
            >
              <PenLine size={14} className="mr-2" /> Open envelope
            </Button>
          ) : (
            <Button
              onClick={openModal}
              className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs"
              data-testid="send-for-signature-btn"
            >
              <PenLine size={14} className="mr-2" /> Send for signature
            </Button>
          )}
        </div>
      </div>

      {sent && (
        <div className="mt-5 pt-5 border-t border-[#E8E3F0] text-sm text-[#215a3b] flex items-center gap-2">
          <CheckCircle2 size={14} /> Envelope sent — {sent.delivered} / {sent.total} invitation{sent.total === 1 ? "" : "s"} delivered via email.
        </div>
      )}

      {open && !sent && (
        <div className="fixed inset-0 bg-[#0B132B]/40 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="send-signature-modal">
          <div className="bg-white max-w-2xl w-full mt-10 border border-[#E8E3F0]">
            <div className="px-6 py-4 border-b border-[#E8E3F0] flex items-center justify-between">
              <div>
                <div className="label-eyebrow">Starter → Signature</div>
                <h3 className="font-serif text-2xl">Send "{draft?.title || starter.title}" for signature</h3>
              </div>
              <button onClick={() => setOpen(false)}><X size={18} /></button>
            </div>

            <div className="p-6 space-y-5">
              {/* Draft preview */}
              <div>
                <div className="label-eyebrow mb-2">The notice (preview)</div>
                {draft ? (
                  <div className="border border-[#E8E3F0] bg-[#FAFAF8] p-4 max-h-[180px] overflow-y-auto whitespace-pre-wrap text-[12px] leading-[1.65] font-mono text-[#2A0F5A]/90">
                    {draft.content?.slice(0, 1500)}
                    {(draft.content?.length || 0) > 1500 && "\n…"}
                  </div>
                ) : (
                  <div className="text-xs text-[#4A5568] flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Loading draft…</div>
                )}
              </div>

              {/* Signers */}
              <div>
                <div className="label-eyebrow mb-2">Signers · in order</div>
                <div className="space-y-2">
                  {signers.map((s, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`conn-signer-${i}`}>
                      <div className="col-span-1 label-eyebrow text-center">{i + 1}</div>
                      <Input value={s.name} onChange={(e) => updateSigner(i, { name: e.target.value })} placeholder="Full name" className="col-span-4 rounded-none border-[#E8E3F0]" data-testid={`conn-signer-name-${i}`} />
                      <Input value={s.email} onChange={(e) => updateSigner(i, { email: e.target.value })} placeholder="email@example.com" className="col-span-5 rounded-none border-[#E8E3F0]" data-testid={`conn-signer-email-${i}`} />
                      <select value={s.role} onChange={(e) => updateSigner(i, { role: e.target.value })} className="col-span-1 border border-[#E8E3F0] px-1 py-2 text-xs bg-white">
                        <option value="signer">S</option>
                        <option value="witness">W</option>
                      </select>
                      <button onClick={() => removeSigner(i)} disabled={signers.length <= 1} className="col-span-1 text-[#4A5568] hover:text-[#8a2121] disabled:opacity-30"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={addSigner}
                  className="text-xs text-[#2A0F5A] hover:underline flex items-center gap-1 mt-2"
                  data-testid="conn-add-signer"
                >
                  <Plus size={11} /> Add signer
                </button>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-[#E8E3F0] flex items-center justify-end gap-3 bg-[#FAFAF8]">
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none border-[#E8E3F0]">Cancel</Button>
              <Button
                onClick={sendIt}
                disabled={busy || !draft}
                className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs"
                data-testid="conn-send-btn"
              >
                {busy ? <><Loader2 size={14} className="mr-2 animate-spin" /> Sending…</> : <>Create envelope &amp; send</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
